import styled from 'styled-components';
import type { SourceId } from '../../constants';
import { getSourceMetaValue } from '../../constants';
import { useData } from '../data';
import type { DefaultComponentProps } from '../types';
import { useSocialFeedSettings } from '../utils';

function errorTemplate(errorMessage: string | Error, platform?: SourceId) {
  if (!platform) {
    return errorMessage;
  }

  const { title: sourceTitle } = getSourceMetaValue(platform);

  return sourceTitle ? `${sourceTitle}: ${errorMessage}` : errorMessage;
}

const SHOW_ON_ONLY_SOURCE = true;

export function WidgetError({ children }: DefaultComponentProps) {
  const { sourcesStates } = useData();
  const { editMode, sources } = useSocialFeedSettings();

  if (children) {
    return (
      <Container>
        <ErrorText>{children}</ErrorText>
      </Container>
    );
  }

  const validatedSources = sources.map((source, i) => {
    if (!source) {
      return {
        source,
        error: 'Source is undefined',
        isValid: false
      };
    }
    const {
      latestError: derivedLatestError,
      latestStatus,
      latestHTTPStatus
    } = sourcesStates?.[i] ?? {};

    const isValid = !['failed', 'rejected'].includes(latestStatus!);

    let error;

    if (latestHTTPStatus === 404) {
      error = errorTemplate('Not found', source.platform);
    }

    if (derivedLatestError?.message) {
      error = errorTemplate(derivedLatestError.message, source.platform);
    }

    if (!isValid) {
      console.error(error);
    }

    return {
      source,
      error: error?.toString(),
      isValid
    };
  });

  const invalidSources = validatedSources.filter(({ isValid }) => !isValid);
  const validSources = validatedSources.filter(({ isValid }) => !!isValid);

  const showSourceErrors = SHOW_ON_ONLY_SOURCE ? validSources.length < 1 : true;

  const emptySourceError = !sources.length;

  if (!editMode) {
    return null;
  }

  if (!showSourceErrors && !emptySourceError) {
    return null;
  }

  return (
    <Container>
      {emptySourceError && (
        <ErrorText>Set at least 1 valid source to display Feed</ErrorText>
      )}

      {showSourceErrors &&
        invalidSources.map(({ error, source }) => (
          <ErrorText key={source?.id}>{error}</ErrorText>
        ))}
    </Container>
  );
}

const ErrorText = styled.div`
  padding: 16px;
  background: #fafafa;
  border-radius: 4px;
  width: 100%;
  text-align: center;
`;

const Container = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & ${ErrorText}:not(:last-child) {
    margin-bottom: 8px;
  }
`;
